import { Button } from '@vrplatform/components';
import { CsvImportModalv2 } from './CsvImportModalv2';
import { useDisclosure } from '@mantine/hooks';
import { type ComponentProps, type ReactNode, useState } from 'react';
import { useBankAccountId } from '../../views/reconciliation/bank-accounts/view/useBankAccountId';
import {
  isBankCsvImportForcedOpened,
  SubmitBankRecordsCsvImportModal,
  useBankImportCsvForcedParam,
} from './CsvImportModal';
import type { TEMPLATES } from './_templates';

type ModalProps = ComponentProps<typeof CsvImportModalv2>;
type UniqueRef = ModalProps['uniqueRef'];

const ModalProps: Record<
  UniqueRef,
  Omit<ModalProps, 'opened' | 'closeModal' | 'onSuccess' | 'uniqueRef'>
> = {
  advancedDeposits: {
    title: 'Import advanced deposits',
    invalidateQueryKeys: ['deposits'],
    invalidateApiQueryGroups: ['deposits', 'accounts'],
    showDateFormatInput: false,
    successMessage: {
      title: 'Advanced deposits imported',
      message: 'Your advanced deposits have been imported successfully',
    },
    description:
      'Upload a CSV file to add payments and refunds to VRPlatform. Your CSV should include header and follow specified structure.',
  },
  bankRecords: {
    title: 'Import bank records',
    invalidateQueryKeys: ['bankRecords'],
    invalidateApiQueryGroups: ['accounts'],
    showDateFormatInput: true,
    successMessage: {
      title: 'Bank records imported',
      message: 'Your bank records have been imported successfully',
    },
  },
  contacts: {
    title: 'Import contacts',
    invalidateQueryKeys: ['contacts'],
    invalidateApiQueryGroups: ['expenses', 'owners'],
    showDateFormatInput: false,
    successMessage: {
      title: 'Contacts imported',
      message: 'Your contacts have been imported successfully',
    },
  },
  deposits: {
    title: 'Import deposits',
    invalidateQueryKeys: ['payments', 'deposits'],
    invalidateApiQueryGroups: ['deposits', 'accounts'],
    showDateFormatInput: true,
    successMessage: {
      title: 'Deposits imported',
      message: 'Your deposits have been imported successfully',
    },
  },
  expense: {
    title: 'Import expenses',
    invalidateQueryKeys: ['expenses', 'accounts'],
    invalidateApiQueryGroups: ['expenses', 'accounts'],
    showDateFormatInput: true,
    successMessage: {
      title: 'Expenses imported',
      message: 'Your expenses have been imported successfully',
    },
  },
  payout: {
    title: 'Import payouts',
    invalidateQueryKeys: ['payouts'],
    invalidateApiQueryGroups: ['deposits'],
    showDateFormatInput: true,
    successMessage: {
      title: 'Payouts imported',
      message: 'Your payouts have been imported successfully',
    },
  },
};

export const CsvImportButton2 = ({
  uniqueRef,
  onSuccess,
  variant = 'transparent',
  children = 'Import v2',
  size,
}: {
  uniqueRef: UniqueRef;
  onSuccess?: ModalProps['onSuccess'];
  variant?: ComponentProps<typeof Button>['variant'];
  children?: ReactNode;
  size?: ComponentProps<typeof Button>['size'];
}) => {
  const [opened, handlers] = useDisclosure(false);

  return (
    <>
      <Button
        onClick={handlers.open}
        leftIcon="CloudUploadIcon"
        variant={variant}
        size={size}
      >
        {children}
      </Button>

      {uniqueRef !== 'bankRecords' && (
        <CsvImportModalv2
          opened={opened}
          closeModal={handlers.close}
          onSuccess={onSuccess}
          uniqueRef={uniqueRef}
          {...ModalProps[uniqueRef]}
        />
      )}
      {uniqueRef === 'bankRecords' && (
        <BankRecordsImportModal
          closeModal={handlers.close}
          onSuccess={onSuccess}
          opened={opened}
        />
      )}
    </>
  );
};

const BankRecordsImportModal = ({
  closeModal: cl,
  onSuccess,
  opened,
}: {
  opened: boolean;
  closeModal: () => void;
  onSuccess: ModalProps['onSuccess'];
}) => {
  const accountId = useBankAccountId();

  const [success, setSuccess] = useState<{
    uploadedRecords: typeof TEMPLATES.bankRecords;
  } | null>(null);

  const [, setForced] = useBankImportCsvForcedParam();

  const forced = isBankCsvImportForcedOpened();

  const closeModal = () => {
    cl();
    setForced(null);
  };

  return (
    <>
      <CsvImportModalv2
        opened={forced || opened}
        closeModal={closeModal}
        {...ModalProps.bankRecords}
        onSuccess={(data) => {
          if (data) {
            setSuccess({
              uploadedRecords: data as typeof TEMPLATES.bankRecords,
            });
          }

          onSuccess?.(data);
        }}
        uniqueRef={'bankRecords'}
        accountId={accountId}
      />
      <SubmitBankRecordsCsvImportModal
        closeCsvImportModal={closeModal}
        success={success}
        setSuccess={setSuccess}
      />
    </>
  );
};
